import { Component, Injectable, inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, ResolveData, Route, RouterStateSnapshot, UrlTree } from '@angular/router';

import { Observable } from 'rxjs';

import { RouteData } from '@hdms/shared/common-types';

import { EnvironmentService } from '../environment/environment.service';
import { PlatformService } from '../platform/platform.service';
import { PreloadType, PreloaderService } from '../preloader-control';

// just a shim to replace page contents
@Component({
  selector: 'shrd-utc-shim',
  template: '',
  standalone: true
})
export class ShimComponent {}

/**
 * On server side in standalone mode this guard removes
 * all guards and resolvers in the current route tree,
 * avoiding potential data requests, and finally
 * replaces the page component with a shim, in order
 * to prevent user-specific contents from being rendered.
 */
export const canMatchEmptyContent: CanMatchFn = route => inject(EmptyRouteGuard).canLoad(route);

/**
 * Important: not intended to be manually applied to route config!
 * It will be added automatically to route via `canMatchEmptyContent` guard invocation.
 */
export const canActivateEmptyContent: CanActivateFn = (_, state) => inject(EmptyRouteGuard).canActivate(state);

/**
 * Dynamically add this guard as `canActivate` to current route config.
 * @param route
 */
function addCanActivateGuard(route: RouteData): void {
  if (route.canActivate) {
    route.canActivate.push(canActivateEmptyContent);
  } else {
    route.canActivate = [canActivateEmptyContent];
  }
}

/**
 * Remove all configured resolvers, except i18next, as it is
 * still required for resolving current translation namespace.
 * @param resolve
 */
function cleanupResolvers(resolve: ResolveData): void {
  resolve &&
    Object.keys(resolve)
      .filter(key => key !== 'i18next')
      .forEach(key => {
        delete resolve[key];
      });
}

/**
 * Remove all guards and resolvers from the route tree
 * and replace the target route component with a shim,
 * @param state
 */
function cleanRouteConfigTree(state: RouterStateSnapshot): void {
  let child = state.root.firstChild;
  do {
    child.routeConfig.canMatch = null;
    child.routeConfig.canActivate = null;
    child.routeConfig.canActivateChild = null;
    cleanupResolvers(child.routeConfig.resolve);
    cleanupResolvers(child['_resolve']); // TODO: Find a more reliable method for clearing resolvers
    child.component &&= ShimComponent;
    child.routeConfig.component &&= ShimComponent;
    child.routeConfig.loadComponent &&= () => ShimComponent;
    child = child.firstChild;
  } while (child);
}

@Injectable({
  providedIn: 'root'
})
export class EmptyRouteGuard {
  protected readonly preloader = inject(PreloaderService);
  protected readonly platform = inject(PlatformService);
  protected readonly env = inject(EnvironmentService);

  canLoad(route: Route): boolean | Observable<boolean | UrlTree> {
    this.env.isStandalone && this.platform.isServer && addCanActivateGuard(route);
    return true;
  }

  canActivate(state: RouterStateSnapshot): boolean | Observable<boolean | UrlTree> {
    this.checkRouteCleanup(state);
    return true;
  }

  /**
   * Empty current route tree and display a preloader,
   * if on server and full SSR rendering not enabled.   *
   * @param state
   * @private
   */
  private checkRouteCleanup(state: RouterStateSnapshot): void {
    if (this.env.ssrCachingEnabled) {
      cleanRouteConfigTree(state);
      this.preloader.startLoading(PreloadType.MANUAL);
    }
  }
}
